import {
  Branding,
  DisplayMode,
  EntityPanelActionHandler,
  EntityTypes,
  FormMode,
  PanelRootContext,
  Property,
  PropertyGroup,
  TraxeroActionResponse,
} from 'types';
import {
  useMediaQuery,
  useTheme,
  MenuItem,
  ListItemIcon,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  disablePermitType,
  createPermitType,
  getEntityPermitTypes,
  updatePermitType,
} from 'api/permitTypes';
import { AppContext } from 'context/appContext';
import MobilePanelList from 'components/lists/MobilePanelList';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ActionFunction,
  Outlet,
  useFetcher,
  useLoaderData,
  useLocation,
  useNavigate,
  useOutletContext,
} from 'react-router';
import { ROUTES, routeWithParams } from 'router';
import { PermitType } from 'types';
import { capitalize, formatCurrency, getBlankPermitType } from 'utils';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  MRT_SortingState,
} from 'material-react-table';
import { Add, History, Delete } from '@mui/icons-material';
import {
  useDefaultTableOptions,
  TableType,
} from 'hooks/useDefaultTableOptions';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import MRTableToolbar from 'components/tables/PanelTableToolbar';

import { useFetcherKey } from 'hooks/useFetcherKey';
import FormHeaderBar from 'components/layout/FormHeaderBar';
export interface PermitPricingLoaderData {
  _permitTypes: Promise<PermitType[]>;
}

export interface PermitTypesRouteParams {
  params: {
    permitTypeID?: string | undefined;
    propertyID?: string | undefined;
    groupID?: string | undefined;
    tenantID?: string | undefined;
  };
}

export interface PermitTypesContext {
  permitType: PermitType;
  setPermitType: (pt: PermitType) => void;
  permitTypes: PermitType[];
  onBack: () => void;
  displayMode: DisplayMode;
  formMode: FormMode;
  fetcherKey: string;
  branding?: Branding;
  serializedEditorState: string | undefined;
  setSerializedEditorState: (editorState: string) => void;
}

export const permitTypesLoader = ({
  params: { tenantID, groupID, propertyID },
}: PermitTypesRouteParams) => {
  if (propertyID && tenantID) {
    return {
      _permitTypes: getEntityPermitTypes({
        tenantID,
        entityType: EntityTypes.Property,
        entityID: propertyID,
      }),
    };
  } else if (groupID && tenantID) {
    return {
      _permitTypes: getEntityPermitTypes({
        tenantID,
        entityType: EntityTypes.PropertyGroup,
        entityID: groupID,
      }),
    };
  } else if (tenantID) {
    return {
      _permitTypes: Promise.resolve([]),
    };
  } else {
    return {
      _permitTypes: Promise.resolve([]),
    };
  }
};

export const PermitTypesPanel = (): ReactNode => {
  const { breakpoints } = useTheme();
  const { t } = useTranslation();
  const mobile = useMediaQuery(breakpoints.down('lg'));
  const { state } = useContext(AppContext);
  const { pathname, state: navState } = useLocation();
  const navigate = useNavigate();
  const { fetcherKey } = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });
  const displayToast = useContext(ToastContext);

  const { formMode, setFormMode, entity, displayMode } =
    useOutletContext<PanelRootContext<Property | PropertyGroup>>();
  const { _permitTypes } = useLoaderData() as PermitPricingLoaderData;

  const [permitType, setPermitType] = useState<PermitType>(
    getBlankPermitType(state.tenant.id),
  );
  const [permitTypes, setPermitTypes] = useState<PermitType[]>(
    entity?.permitTypes || [],
  );
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [serializedEditorState, setSerializedEditorState] = useState<
    string | undefined
  >();
  const canAddRemove = useMemo<boolean>(() => {
    if (displayMode === 'property') {
      return state.entityPermissions.property.update;
    } else if (displayMode === 'group') {
      return state.entityPermissions.property_group.update;
    }
    return false;
  }, [state.entityPermissions, displayMode]);

  useEffect(() => {
    awaitLoaderData();
  }, []);

  const awaitLoaderData = async () => {
    const permitTypes = await _permitTypes;
    setPermitTypes(permitTypes);
    if (navState?.targetID) {
      const permitType = permitTypes.find(
        (p) => p.id === parseInt(navState.targetID, 10),
      );
      if (permitType) {
        setPermitType(permitType);
      }
      setFormMode('edit');
      navigate(
        routeWithParams(ROUTES.PROPERTY_GROUP_PERMIT_TYPES_FORM, [
          state.tenant.id,
          navState.groupID,
          navState.targetID,
        ]),
        { replace: true },
      );
    }
  };

  const getSuccessMessage = useCallback(
    (res: TraxeroActionResponse<EntityPanelActionHandler>): string => {
      switch (res.handler) {
        case EntityPanelActionHandler.UPDATE:
          return t('update_success', {
            ns: 'toasts',
            name: `${t('permit_type.label', { ns: 'entities' })} ${
              permitType.name
            }`,
          });
        case EntityPanelActionHandler.DELETE:
          return t('delete_success', {
            ns: 'toasts',
            name: `${t('permit_type.label', { ns: 'entities' })} ${
              permitType.name
            }`,
          });
        default:
          return t('create_success', {
            ns: 'toasts',
            name: `${t('permit_type.label', { ns: 'entities' })} ${
              permitType.name
            }`,
          });
      }
    },
    [],
  );
  const getErrorMessage = useCallback(
    (res: TraxeroActionResponse<EntityPanelActionHandler>): string => {
      const { handler, status, statusText } = res;
      switch (handler) {
        case EntityPanelActionHandler.UPDATE:
          return t('update_error', {
            name: `${t('permit_type.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.DELETE:
          return t('toast.deleteError', {
            name: `${t('permit_type.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        default:
          return t('create_error', {
            name: `${t('permit_type.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
      }
    },
    [],
  );
  const response =
    fetcher.data as TraxeroActionResponse<EntityPanelActionHandler> | null;
  useEffect(() => {
    if (response) {
      if (response.status < 400) {
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: getSuccessMessage(response),
        });
        handleCancelAction();
        awaitLoaderData();
      } else {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: getErrorMessage(response),
        });
      }
    }
  }, [fetcher]);

  const handleCancelAction = useCallback(() => {
    setFormMode('none');
    setSerializedEditorState(undefined);
    setPermitType(getBlankPermitType(state.tenant.id));
    // resolve the list route explicitly -- reading the current pathname here
    // would point at the form route the user is trying to leave
    if (navState?.propertyID) {
      navigate(
        routeWithParams(ROUTES.PROPERTY_PERMIT_TYPES, [
          state.tenant.id,
          navState.propertyID,
        ]),
        { replace: true },
      );
    } else {
      navigate(
        routeWithParams(
          displayMode === 'group'
            ? ROUTES.PROPERTY_GROUP_PERMIT_TYPES
            : ROUTES.PROPERTY_PERMIT_TYPES,
          [state.tenant.id, entity?.id],
        ),
        { replace: true },
      );
    }
  }, [
    navState?.propertyID,
    displayMode,
    state.tenant.id,
    entity?.id,
    navigate,
  ]);

  const handleCreateClick = () => {
    setFormMode('create');
    setPermitType(getBlankPermitType(state.tenant.id));
    navigate(`${pathname}/0`);
  };

  const handleDeleteClick = (id: number | undefined) => {
    if (!id) return;

    let action: string;
    if (displayMode === 'property') {
      action = routeWithParams(ROUTES.PROPERTY_PERMIT_TYPES, [
        state.tenant.id,
        entity?.id,
      ]);
    } else {
      action = routeWithParams(ROUTES.PROPERTY_GROUP_PERMIT_TYPES, [
        state.tenant.id,
        entity?.id,
      ]);
    }
    fetcher.submit(
      {
        body: id,
        handler: EntityPanelActionHandler.DELETE,
      },
      {
        method: 'delete',
        action,
        encType: 'application/json',
      },
    );
  };

  const handleViewRevisions = (id: number | undefined) => {
    if (!id) return;
    navigate(
      routeWithParams(ROUTES.REVISIONS, [
        state.tenant.id,
        EntityTypes.PermitType,
        `${id}`,
      ]),
    );
  };

  const handlePermitTypeClick = (id: number | undefined) => {
    if (!id) return;
    const selected: PermitType | undefined = permitTypes.find(
      (p) => p.id === id,
    );
    if (selected) {
      setPermitType(selected);
      if (
        selected.data?.termsAndConditions &&
        typeof selected.data.termsAndConditions === 'string'
      ) {
        setSerializedEditorState(selected.data.termsAndConditions);
      }
      setFormMode('edit');
      if (displayMode === 'group') {
        navigate(
          routeWithParams(ROUTES.PROPERTY_GROUP_PERMIT_TYPES_FORM, [
            state.tenant.id,
            entity?.id,
            selected?.id || 0,
          ]),
        );
      } else if (selected.ancestry?.propertyGroupID) {
        navigate(
          routeWithParams(ROUTES.PROPERTY_GROUP_PERMIT_TYPES, [
            state.tenant.id,
            selected.ancestry?.propertyGroupID,
          ]),
          {
            state: {
              targetID: selected.id,
              groupID: selected.ancestry?.propertyGroupID,
              propertyID: entity?.id,
            },
          },
        );
      } else {
        navigate(
          routeWithParams(ROUTES.PROPERTY_PERMIT_TYPES_FORM, [
            state.tenant.id,
            entity?.id,
            selected?.id || 0,
          ]),
        );
      }
      return;
    } else {
      displayToast({
        type: TOAST_TYPE.ERROR,
        message: t('toast.findError', {
          name: t('pricing.label'),
          error: t('errors.notFound'),
        }),
      });
    }
  };

  const getPriceLabel = useCallback((row: PermitType) => {
    if (!row?.permitPricing?.pricePerUnit) {
      return `${t('Free')}`;
    }
    const currency = formatCurrency(row.permitPricing?.pricePerUnit);
    if (
      row.permitPricing?.unitOfTime === 'forever' ||
      !row.permitPricing?.unitOfTime
    ) {
      return currency;
    }
    const perUnitString = `${row.data?.defaultTerm?.amountOfTime} ${t(
      `timeUnit.${row.permitPricing?.unitOfTime}`,
      { ns: 'enums', count: row.data?.defaultTerm?.amountOfTime },
    )}`;
    return `${currency}/${perUnitString}`;
  }, []);

  const columns = useMemo<MRT_ColumnDef<PermitType>[]>(() => {
    const _columns: MRT_ColumnDef<PermitType>[] = [
      {
        header: t('permit_type.id', { ns: 'entities' }),
        accessorKey: 'id',
      },
      {
        header: t('permit_type.name', { ns: 'entities' }),
        accessorKey: 'name',
      },
      {
        header: t('permit_type.description', { ns: 'entities' }),
        accessorKey: 'description',
      },
      {
        header: t('permit_type.data.quantityOfSpots', { ns: 'entities' }),
        id: 'quantityOfSpots',
        Cell: ({ row }) => {
          const quantityOfSpots = row.original.data?.quantityOfSpots;
          return quantityOfSpots && quantityOfSpots > 0
            ? quantityOfSpots
            : t('labels.unlimited', { ns: 'permitTypes' });
        },
      },
      {
        header: t('labels.price_per_unit', { ns: 'permitTypes' }),
        id: 'pricePerUnit',
        Cell: ({ row }) => getPriceLabel(row.original),
      },
      {
        header: t('labels.default_term', { ns: 'permitTypes' }),
        id: 'unitOfTime',
        accessorFn: (row) => row.permitPricing?.unitOfTime || '-',
        Cell: ({ row }) => {
          const unitOfTime = row.original.data?.defaultTerm?.unitOfTime;
          const amountOfTime = row.original.data?.defaultTerm?.amountOfTime;
          if (unitOfTime === 'forever' || !unitOfTime) {
            return capitalize(t('timeUnit.forever', { ns: 'enums' }));
          }
          return `${amountOfTime} ${t(`timeUnit.${unitOfTime}`, {
            ns: 'enums',
            count: amountOfTime,
          })}`;
        },
      },
      {
        header: t('permit_type.data.selfService.label', { ns: 'entities' }),
        accessorKey: 'data.selfService',
        id: 'selfService',
        Cell: ({ renderedCellValue }) => {
          return renderedCellValue ? t('yes') : t('no');
        },
      },
      {
        header: t('labels.needs_approval', { ns: 'permitTypes' }),
        id: 'needsApproval',
        accessorKey: 'data.needsApproval',
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? t('yes') : t('no'),
      },
      {
        header: t('permit_type.created', { ns: 'entities' }),
        accessorKey: 'created',
        accessorFn: (row) => (row.created ? new Date(row.created) : null),
        Cell: ({ cell }) =>
          cell.getValue<Date>()
            ? dayjs(cell.getValue<Date>()).format('MM/DD/YYYY')
            : '',
        filterVariant: 'date',
        filterFn: (row, id, filterValue) => {
          return dayjs(row.getValue(id)).isSame(dayjs(filterValue), 'day');
        },
      },
      {
        header: t('permit_type.updated', { ns: 'entities' }),
        accessorKey: 'updated',
        accessorFn: (row) => (row.updated ? new Date(row.updated) : null),
        Cell: ({ cell }) =>
          cell.getValue<Date>()
            ? dayjs(cell.getValue<Date>()).format('MM/DD/YYYY')
            : '',
        filterVariant: 'date',
        filterFn: (row, id, filterValue) => {
          return dayjs(row.getValue(id)).isSame(dayjs(filterValue), 'day');
        },
      },
    ];
    if (displayMode === 'property') {
      _columns.push({
        header: t('from', {
          name: t('property_group.label', { ns: 'entities' }),
        }),
        accessorKey: 'ancestry.propertyGroupName',
        id: 'propertyGroupName',
        accessorFn: (row) => row.ancestry?.propertyGroupName || '-',
      });
    }
    return _columns;
  }, [displayMode]);
  const tableDefaults = useDefaultTableOptions<PermitType>();
  const table = useMaterialReactTable({
    ...tableDefaults,
    data: permitTypes,
    columns,
    enableRowSelection: false,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: true,
      columnVisibility: {
        id: false,
        name: true,
        description: true,
        quantityOfSpots: true,
        pricePerUnit: true,
        unitOfTime: true,
        created: false,
        updated: false,
        propertyGroupName: false,
      },
      columnPinning: {
        right: ['mrt-row-actions'],
      },
    },
    state: {
      isLoading: fetcher.state === 'loading',
      sorting,
    },
    onSortingChange: setSorting,
    getRowId: (row) => `${row.id}`,
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: 'white',
        cursor: 'pointer',
      },
      onClick: () => handlePermitTypeClick(row.original.id),
    }),
    renderTopToolbar: ({ table }) => (
      <MRTableToolbar
        table={table}
        isSubmitting={fetcher.state === 'submitting'}
        tableType={TableType.PANEL}
        menuOptions={[
          {
            onClick: () => handleCreateClick(),
            content: t('buttons.create', {
              name: t('permit_type.label', { ns: 'entities' }),
            }),
            disabled: !state.entityPermissions.permit_type.create,
            icon: <Add />,
          },
        ]}
      />
    ),
    renderRowActionMenuItems: ({ closeMenu, row }) => {
      const options = [
        <MenuItem
          key={1}
          divider
          onClick={() => {
            handleViewRevisions(row.original.id);
            closeMenu();
          }}
          sx={{ m: 0, backgroundColor: 'white' }}
        >
          <ListItemIcon>
            <History />
          </ListItemIcon>
          {t('buttons.view', { name: t('revision.label_plural') })}
        </MenuItem>,
        <MenuItem
          key={2}
          divider
          onClick={() => {
            handleDeleteClick(row.original.id);
            closeMenu();
          }}
          sx={{ m: 0, backgroundColor: 'white' }}
          disabled={!canAddRemove || !!row.original.ancestry?.propertyGroupID}
        >
          <ListItemIcon>
            <Delete />
          </ListItemIcon>
          {t('buttons.delete', {
            name: t('permit_type.label', { ns: 'entities' }),
          })}
        </MenuItem>,
      ];
      return options;
    },
  });

  if (formMode !== 'none') {
    return (
      <React.Fragment>
        <FormHeaderBar />
        <Outlet
          context={
            {
              displayMode,
              permitTypes,
              permitType,
              setPermitType,
              onBack: handleCancelAction,
              formMode,
              branding: entity?.data?.branding,
              serializedEditorState,
              setSerializedEditorState,
              fetcherKey,
            } satisfies PermitTypesContext
          }
        />
      </React.Fragment>
    );
  }

  if (mobile) {
    const itemHeight = 70;
    const renderItem = (item: PermitType) => {
      return (
        <ListItem
          key={item.id}
          sx={{ height: itemHeight, backgroundColor: 'white' }}
          divider
          onClick={() => handlePermitTypeClick(item.id)}
        >
          <ListItemText
            primary={item.name}
            secondary={item.description}
            primaryTypographyProps={{
              variant: 'subtitle1',
              noWrap: true,
            }}
            secondaryTypographyProps={{
              variant: 'subtitle2',
              noWrap: true,
            }}
          />
        </ListItem>
      );
    };

    return (
      <MobilePanelList
        renderItem={renderItem}
        itemHeight={itemHeight}
        columns={columns.map((c) => ({
          name: c.header as string,
          searchKey: c.accessorKey as string,
        }))}
        withSearchBar
        title={t('permit_type.label')}
        data={permitTypes}
        menuOptions={[
          {
            onClick: handleCreateClick,
            content: t('buttons.create', { name: t('permit_type.label') }),
            disabled: !canAddRemove,
            icon: <Add />,
          },
        ]}
      />
    );
  }

  return <MaterialReactTable table={table} />;
};

export const permitTypesAction: ActionFunction = async ({
  params,
  request,
}): Promise<TraxeroActionResponse<EntityPanelActionHandler>> => {
  const { body, handler } = await request.json();
  const { tenantID, propertyID, groupID, permitTypeID } =
    params as PermitTypesRouteParams['params'];
  let entityType: EntityTypes;
  let entityID: number;

  if (propertyID) {
    entityType = EntityTypes.Property;
    entityID = parseInt(propertyID, 10);
  } else if (groupID) {
    entityType = EntityTypes.PropertyGroup;
    entityID = parseInt(groupID, 10);
  } else {
    entityType = EntityTypes.None;
    entityID = 0;
  }

  let res: Response;
  try {
    switch (handler) {
      case EntityPanelActionHandler.DELETE:
        res = await disablePermitType({
          tenantID,
          entityType,
          entityID,
          permitTypeID: body,
        });
        break;
      case EntityPanelActionHandler.UPDATE:
        res = await updatePermitType({
          tenantID,
          body,
          entityType,
          entityID,
          permitTypeID,
        });
        break;
      case EntityPanelActionHandler.GROUP_CREATE:
      case EntityPanelActionHandler.PROPERTY_CREATE:
        res = await createPermitType({
          tenantID,
          body,
          entityType,
          entityID,
        });
        break;
      default: {
        res = new Response(null, {
          status: 404,
          statusText: 'Unknown Handler',
        });
      }
    }
  } catch (e) {
    res = new Response(null, { status: 500, statusText: 'Network Error' });
  }

  return { handler, status: res.status, statusText: res.statusText };
};
